import { useEffect, useCallback } from 'react';
import { canMeasure } from '@/lib/trackingPrivacy';

export interface UTMParams {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  ttclid: string | null;
  referrer: string | null;
}

const UTM_STORAGE_KEY = 'utm_params';
export const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
const permitted = () => canMeasure('marketing');
const bounded = (value: string | null) => value && /^[\p{L}\p{N} _.,:+/-]{1,120}$/u.test(value) && !value.includes('@') ? value : null;

const EMPTY_PARAMS: UTMParams = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  utm_term: null,
  utm_content: null,
  gclid: null,
  gbraid: null,
  wbraid: null,
  fbclid: null,
  msclkid: null,
  ttclid: null,
  referrer: null,
};

// Capture UTM parameters from URL and store in sessionStorage
export const captureUTMParams = (): UTMParams => {
  if (!permitted()) { sessionStorage.removeItem(UTM_STORAGE_KEY); return { ...EMPTY_PARAMS }; }
  const urlParams = new URLSearchParams(window.location.search);

  const params: UTMParams = {
    utm_source: urlParams.get('utm_source'),
    utm_medium: urlParams.get('utm_medium'),
    utm_campaign: urlParams.get('utm_campaign'),
    utm_term: urlParams.get('utm_term'),
    utm_content: urlParams.get('utm_content'),
    gclid: urlParams.get('gclid'),
    gbraid: urlParams.get('gbraid'),
    wbraid: urlParams.get('wbraid'),
    fbclid: urlParams.get('fbclid'),
    msclkid: urlParams.get('msclkid'),
    ttclid: urlParams.get('ttclid'),
    referrer: null,
  };

  for (const key of Object.keys(params) as Array<keyof UTMParams>) params[key] = bounded(params[key]);
  params.utm_term = null; params.fbclid = null; params.ttclid = null; params.msclkid = null;
  const hasParams = Object.values(params).some((v) => v !== null && v !== '');

  if (hasParams) {
    const existing = getStoredUTMParams();
    const merged: UTMParams = {
      utm_source: params.utm_source || existing.utm_source,
      utm_medium: params.utm_medium || existing.utm_medium,
      utm_campaign: params.utm_campaign || existing.utm_campaign,
      utm_term: params.utm_term || existing.utm_term,
      utm_content: params.utm_content || existing.utm_content,
      gclid: params.gclid || existing.gclid,
      gbraid: params.gbraid || existing.gbraid,
      wbraid: params.wbraid || existing.wbraid,
      fbclid: params.fbclid || existing.fbclid,
      msclkid: params.msclkid || existing.msclkid,
      ttclid: params.ttclid || existing.ttclid,
      referrer: existing.referrer || params.referrer,
    };

    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ params: merged, expiresAt: Date.now() + ATTRIBUTION_TTL_MS }));
    return merged;
  }

  return getStoredUTMParams();
};

// Get stored UTM parameters from sessionStorage
export const getStoredUTMParams = (): UTMParams => {
  try {
    if (!permitted()) { sessionStorage.removeItem(UTM_STORAGE_KEY); return { ...EMPTY_PARAMS }; }
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { expiresAt?: number; params?: Partial<UTMParams> };
      if (!parsed.expiresAt || parsed.expiresAt <= Date.now() || parsed.expiresAt > Date.now() + ATTRIBUTION_TTL_MS || !parsed.params) { sessionStorage.removeItem(UTM_STORAGE_KEY); return { ...EMPTY_PARAMS }; }
      return { ...EMPTY_PARAMS, ...parsed.params };
    }
  } catch (e) {
    console.warn('Error parsing stored UTM params:', e);
  }
  return { ...EMPTY_PARAMS };
};

// Get UTM source for lead tracking (prioritizes click IDs)
export const getLeadSource = (): string => {
  const params = getStoredUTMParams();

  if (params.gclid || params.gbraid || params.wbraid) return 'google_ads';
  if (params.fbclid) return 'facebook_ads';
  if (params.msclkid) return 'microsoft_ads';
  if (params.ttclid) return 'tiktok_ads';
  if (params.utm_source) return params.utm_source;
  if (params.referrer) {
    try {
      const referrerUrl = new URL(params.referrer);
      const hostname = referrerUrl.hostname.toLowerCase();
      if (hostname.includes('google')) return 'google_organic';
      if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'facebook';
      if (hostname.includes('instagram')) return 'instagram';
      if (hostname.includes('twitter') || hostname.includes('x.com')) return 'twitter';
      if (hostname.includes('linkedin')) return 'linkedin';
      if (hostname.includes('youtube')) return 'youtube';
      return referrerUrl.hostname;
    } catch {
      return 'referral';
    }
  }
  return 'direct';
};

// Get UTM data for form submission (flattened for easy use)
export const getUTMDataForForm = () => {
  const params = getStoredUTMParams();
  return {
    source: getLeadSource(),
    utm_source: params.utm_source || undefined,
    utm_medium: params.utm_medium || undefined,
    utm_campaign: params.utm_campaign || undefined,
    utm_term: params.utm_term || undefined,
    utm_content: params.utm_content || undefined,
    gclid: params.gclid || undefined,
    gbraid: params.gbraid || undefined,
    wbraid: params.wbraid || undefined,
    fbclid: params.fbclid || undefined,
    msclkid: params.msclkid || undefined,
    ttclid: params.ttclid || undefined,
    referrer: params.referrer || undefined,
  };
};

// Returns hidden-field entries for embedding into <form> elements
export const getUTMHiddenFieldEntries = (): Array<{ name: string; value: string }> => {
  const data = getUTMDataForForm();
  return Object.entries(data)
    .filter(([, v]) => typeof v === 'string' && v.length > 0)
    .map(([name, value]) => ({ name, value: String(value) }));
};

// Hook for capturing UTM params on mount
export const useUTMTracking = () => {
  useEffect(() => {
    captureUTMParams();
  }, []);

  const getUTMParams = useCallback(() => getStoredUTMParams(), []);
  const getSource = useCallback(() => getLeadSource(), []);
  const getFormData = useCallback(() => getUTMDataForForm(), []);
  const getHiddenFields = useCallback(() => getUTMHiddenFieldEntries(), []);

  return {
    getUTMParams,
    getSource,
    getFormData,
    getHiddenFields,
  };
};

export default useUTMTracking;
